// ─── styles.js — Theme-aware style recipes ────────────────────────────────────
// Usage:
//   import { sx, row, card, subText, heading } from "../lib/styles.js";
//
//   <div style={row(8)}>               // flex row, gap 8
//   <div style={card(T)}>              // standard card surface
//   <div style={sx(row(8), {padding:16})}>  // compose + extend
//   <div style={subText(T)}>           // muted small text
//   <input style={inputStyle(T)}>      // standard themed input

import { FONT, RADIUS } from "./theme.js";

// ─── Composer ─────────────────────────────────────────────────────────────────
/** Merge any number of style objects, skipping null/undefined. */
export function sx(...styles) {
  return Object.assign({}, ...styles.filter(Boolean));
}

// ─── Layout recipes (no theme needed) ─────────────────────────────────────────

/** Flex row with optional gap. */
export function row(gap = 8, extra = {}) {
  return { display: "flex", alignItems: "center", gap, ...extra };
}

/** Flex row that wraps. */
export function rowWrap(gap = 12, extra = {}) {
  return { display: "flex", alignItems: "center", flexWrap: "wrap", gap, ...extra };
}

/** Flex column with optional gap. */
export function col(gap = 12, extra = {}) {
  return { display: "flex", flexDirection: "column", gap, ...extra };
}

/** Space-between row. */
export function rowBetween(gap = 8, extra = {}) {
  return { display: "flex", alignItems: "center", justifyContent: "space-between", gap, ...extra };
}

// ─── Themed recipes (require T from useTheme()) ───────────────────────────────

/** Standard card surface. */
export function card(T, extra = {}) {
  return {
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.lg,
    padding: 20,
    ...extra,
  };
}

/** Subtle surface (slightly darker than card). */
export function surface(T, extra = {}) {
  return {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.md,
    ...extra,
  };
}

/** Primary heading text. */
export function heading(T, extra = {}) {
  return {
    fontSize: 19,
    fontWeight: 700,
    color: T.text,
    letterSpacing: -0.3,
    ...extra,
  };
}

/** Section sub-heading. */
export function subHeading(T, extra = {}) {
  return {
    fontSize: 15,
    fontWeight: 600,
    color: T.text,
    ...extra,
  };
}

/** Small muted text (labels, hints, timestamps). */
export function subText(T, extra = {}) {
  return { fontSize: 12, color: T.textSub, ...extra };
}

/** Even smaller muted text. */
export function microText(T, extra = {}) {
  return { fontSize: 11, color: T.textSub, ...extra };
}

/** Standard inline input. */
export function inputStyle(T, extra = {}) {
  return {
    background: T.surface,
    border: `1.5px solid ${T.border}`,
    borderRadius: RADIUS.md,
    color: T.text,
    fontFamily: FONT,
    fontSize: 14,
    outline: "none",
    padding: "8px 12px",
    transition: "border-color 0.15s",
    ...extra,
  };
}

/** Small inline input (used in table rows). */
export function inputSmall(T, extra = {}) {
  return inputStyle(T, { fontSize: 13, padding: "5px 8px", ...extra });
}

/** Icon button (square, no border). */
export function iconBtn(T, extra = {}) {
  return {
    background: "transparent",
    border: "none",
    borderRadius: RADIUS.sm,
    color: T.textSub,
    cursor: "pointer",
    fontSize: 14,
    padding: "4px 6px",
    transition: "background 0.12s, color 0.12s",
    ...extra,
  };
}

/** Clickable row that highlights on hover — apply via onMouseEnter/Leave. */
export function hoverRow(T) {
  return {
    enter: { background: T.surfaceHi },
    leave: { background: "transparent" },
  };
}

/** Standard section header row (title left, action right, bottom margin). */
export function sectionHeader(T, extra = {}) {
  return rowBetween(8, { marginBottom: 16, ...extra });
}

/** Badge / pill chip. */
export function chip(color, extra = {}) {
  return {
    background: color + "20",
    color,
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: RADIUS.pill,
    letterSpacing: 0.3,
    ...extra,
  };
}

/** Danger / warning chip. */
export function chipDanger(T, extra = {}) {
  return chip(T.red, extra);
}

/** Amount color: green for positive, red for negative, muted for zero. */
export function amountColor(T, value) {
  if (value > 0) return T.green;
  if (value < 0) return T.red;
  return T.textSub;
}

/** Table header cell style. */
export function thStyle(T, align = "right", extra = {}) {
  return {
    padding: "8px 14px",
    textAlign: align,
    color: T.textSub,
    fontWeight: 600,
    borderBottom: `1px solid ${T.border}`,
    fontSize: 10,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    background: T.card,
    ...extra,
  };
}

/** Table data cell style. */
export function tdStyle(T, align = "right", extra = {}) {
  return {
    padding: "7px 14px",
    textAlign: align,
    borderBottom: `1px solid ${T.border}`,
    ...extra,
  };
}

/** Glassmorphism card — for panels and overlays. */
export function glassCard(T, extra = {}) {
  return {
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: RADIUS.lg,
    padding: 20,
    ...extra,
  };
}

/** Violet accent pill button. */
export function accentPill(T, active = false, extra = {}) {
  return {
    background: active ? T.accent : T.accent + "18",
    color: active ? "#fff" : T.accent,
    border: `1px solid ${active ? T.accent : T.accent + "35"}`,
    borderRadius: RADIUS.pill,
    padding: "6px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT,
    transition: "all 0.15s ease",
    ...extra,
  };
}
